/**
 * TopBar Component - Redesigned with Hook-Based Architecture
 *
 * This component has been redesigned to eliminate the complex registration system
 * in favor of a simpler prop-based approach:
 *
 * - Actions are passed as props instead of being registered via context
 * - Each component exposes a custom hook (e.g., useWorkflowResultsActions)
 *   that returns its actions
 * - Parent components orchestrate by calling hooks and passing actions to TopBar
 * - Optional animation system with configurable presets
 * - TypeScript-safe action IDs via enum
 *
 * Benefits:
 * - Clear separation of concerns
 * - No hidden state or complex lifecycle management
 * - Better performance (no effect cycles)
 * - Easier testing and debugging
 * - Improved TypeScript safety
 */

import { useTooltip } from '@/hooks/useTooltip'
import { motion } from 'framer-motion'
import React from 'react'
import { createPortal } from 'react-dom'
import { TopBarActionId, validateActionIds } from './TopBarActionIds'
import TopBarAnimationWrapper from './TopBarAnimation'

export interface TopBarAction {
  id: TopBarActionId | string // Allow string for backward compatibility
  element: React.ReactNode
  visible?: boolean
  order?: number
}

interface TopBarProps {
  leftActions?: TopBarAction[]
  leftContent?: React.ReactNode
  centerContent?: React.ReactNode
  rightActions?: TopBarAction[]
  rightContent?: React.ReactNode
  animated?: boolean
  animationType?: 'stagger' | 'fade' | 'slide' | 'none'
  animationProps?: {
    duration?: number
    staggerDelay?: number
    ease?: any
  }
}

interface TopBarToggleButtonProps {
  onClick: () => void
  isActive: boolean
  title: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
}

interface TopBarToggleSwitchProps<T extends string> {
  mode: T
  onModeChange: (mode: T) => void
  options: Array<{
    value: T
    label: string
  }>
  width?: string
  className?: string
}

interface TopBarButtonProps {
  onClick: () => void
  title: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
}

export function TopBarToggleButton({
  className,
  onClick,
  isActive,
  title,
  children,
  disabled = false,
}: TopBarToggleButtonProps) {
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } =
    useTooltip()

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={() => {
          if (title) {
            showTooltipWithDelay()
          }
        }}
        onMouseLeave={hideTooltip}
        className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors sm:h-8 sm:w-8 ${
          isActive
            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } ${className}`}
        disabled={disabled}
      >
        {children}
      </button>
      {showTooltip &&
        title &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {title}
          </div>,
          document.body
        )}
    </>
  )
}

export function TopBarButton({
  className,
  onClick,
  title,
  children,
  disabled = false,
}: TopBarButtonProps) {
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } =
    useTooltip()

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={() => {
          if (title) {
            showTooltipWithDelay()
          }
        }}
        onMouseLeave={hideTooltip}
        className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 sm:h-8 sm:w-8 ${
          disabled ? 'cursor-not-allowed opacity-50 hover:bg-gray-100' : ''
        } ${className}`}
        disabled={disabled}
      >
        {children}
      </button>
      {showTooltip &&
        title &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {title}
          </div>,
          document.body
        )}
    </>
  )
}

export function TopBarToggleSwitch<T extends string>({
  mode,
  onModeChange,
  options,
  width = 'w-full sm:w-80',
  className,
}: TopBarToggleSwitchProps<T>) {
  return (
    <div
      className={`flex h-10 ${width} min-w-0 max-w-full rounded-lg border border-black/10 bg-gray-200/70 p-0.5 sm:h-8 ${className}`}
    >
      {options.map((option) => (
        <motion.button
          key={option.value}
          className="relative flex h-9 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-[7px] sm:h-7"
          type="button"
          aria-pressed={mode === option.value}
          onClick={() => onModeChange(option.value)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          {mode === option.value && (
            <motion.div
              layoutId="active-background"
              className="absolute inset-0 rounded-[7px] border border-black/5 bg-white shadow-md"
              initial={false}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 35,
              }}
            />
          )}
          <motion.span
            className={`relative z-10 truncate px-2 text-sm ${
              mode === option.value ? 'font-semibold text-black' : 'font-medium text-black'
            }`}
            animate={{
              fontWeight: mode === option.value ? 600 : 500,
            }}
            transition={{ duration: 0.2 }}
          >
            {option.label}
          </motion.span>
        </motion.button>
      ))}
    </div>
  )
}

export function TopBar({
  leftActions,
  leftContent,
  centerContent,
  rightActions,
  rightContent,
  animated = false,
  animationType = 'stagger',
  animationProps,
}: TopBarProps) {
  // Sort actions by order
  const sortedLeftActions = leftActions?.sort((a, b) => (a.order || 0) - (b.order || 0)) || []

  const sortedRightActions = rightActions?.sort((a, b) => (a.order || 0) - (b.order || 0)) || []

  // Development-mode validation
  React.useEffect(() => {
    const allActionIds = [...sortedLeftActions, ...sortedRightActions].map((action) =>
      action.id.toString()
    )
    validateActionIds(allActionIds)
  }, [sortedLeftActions, sortedRightActions])

  // Render actions with optional animation
  const renderActions = (actions: TopBarAction[]) => {
    if (animated) {
      return (
        <TopBarAnimationWrapper
          actions={actions}
          animationType={animationType}
          animationProps={animationProps}
        />
      )
    }

    return (
      <>
        {actions
          .filter((action) => action.visible !== false)
          .map((action) => (
            <React.Fragment key={action.id}>{action.element}</React.Fragment>
          ))}
      </>
    )
  }

  return (
    <div className="flex h-auto flex-shrink-0 flex-col items-stretch gap-2 rounded-t-[20px] bg-white/20 p-2 sm:flex-row sm:items-center sm:gap-3 sm:p-4">
      {/* Left Content */}
      <div className="flex min-w-0 items-center gap-2 sm:flex-1">
        {renderActions(sortedLeftActions)}
        {leftContent}
      </div>

      {/* Center Content */}
      {centerContent && (
        <div className="flex min-w-0 items-center justify-center sm:flex-1">{centerContent}</div>
      )}

      {/* Right Content */}
      <div className="flex min-w-0 items-center justify-end gap-2 sm:flex-1">
        {renderActions(sortedRightActions)}
        {rightContent}
      </div>
    </div>
  )
}
